import { useEffect } from 'react';
import Plans from '../components/Plans';
import './PlansPage.css';
import { setPageTitle, pageTitles, pageDescriptions } from '../utils/pageTitle';
import { useLanguage } from '../contexts/LanguageContext';

const PlansPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    setPageTitle(pageTitles.plans, pageDescriptions.plans, 'plans', language);
  }, [language]);

  const faqs = [
    {
      question: t('plans.faqs.choosePlan.question'),
      answer: t('plans.faqs.choosePlan.answer')
    },
    {
      question: t('plans.faqs.switchPlans.question'),
      answer: t('plans.faqs.switchPlans.answer')
    },
    {
      question: t('plans.faqs.rdService.question'),
      answer: t('plans.faqs.rdService.answer')
    },
    {
      question: t('plans.faqs.international.question'),
      answer: t('plans.faqs.international.answer')
    },
    {
      question: t('plans.faqs.technologies.question'),
      answer: t('plans.faqs.technologies.answer')
    },
    {
      question: t('plans.faqs.quality.question'),
      answer: t('plans.faqs.quality.answer')
    }
  ];

  const testimonials = [
    {
      name: t('plans.testimonials.ad.name'),
      company: t('plans.testimonials.ad.company'),
      role: t('plans.testimonials.ad.role'),
      content: t('plans.testimonials.ad.content'),
      rating: 5
    },
    {
      name: t('plans.testimonials.de.name'),
      company: t('plans.testimonials.de.company'),
      role: t('plans.testimonials.de.role'),
      content: t('plans.testimonials.de.content'),
      rating: 5
    },
    {
      name: t('plans.testimonials.assaf.name'),
      company: t('plans.testimonials.assaf.company'),
      role: t('plans.testimonials.assaf.role'),
      content: t('plans.testimonials.assaf.content'),
      rating: 5
    },
    {
      name: t('plans.testimonials.gh.name'),
      company: t('plans.testimonials.gh.company'),
      role: t('plans.testimonials.gh.role'),
      content: t('plans.testimonials.gh.content'),
      rating: 5
    },
    {
      name: t('plans.testimonials.js.name'),
      company: t('plans.testimonials.js.company'),
      role: t('plans.testimonials.js.role'),
      content: t('plans.testimonials.js.content'),
      rating: 5
    }
  ];

  return (
    <div className="plans-page">
      <section className="plans-hero section">
        <div className="container">
          <div className="plans-hero-content">
            <h1 className="page-title">{t('plans.pageTitle')}</h1>
            <p className="plans-hero-description">
              {t('plans.pageDescription')}
            </p>
          </div>
        </div>
      </section>

      <Plans />

      <section className="comparison-section section">
        <div className="container">
          <h2 className="section-title">{t('plans.comparisonTitle')}</h2>
          <div className="table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr className="comparison-header">
                  <th className="feature-column">{t('plans.comparison.features')}</th>
                  <th className="plan-column">{t('plans.comparison.startupMvp')}</th>
                  <th className="plan-column">{t('plans.comparison.engagement')}</th>
                  <th className="plan-column">{t('plans.comparison.professional')}</th>
                  <th className="plan-column">{t('plans.comparison.custom')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.fullStackDev')}</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.projectManagement')}</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.devOpsInfra')}</td>
                  <td className="feature-value">{t('plans.comparison.basic')}</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.uxUiDesign')}</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.aiMlIntegration')}</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">{t('plans.comparison.optional')}</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.dedicatedTeam')}</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">✓</td>
                </tr>

                <tr className="comparison-row">
                  <td className="feature-name">{t('plans.comparison.support247')}</td>
                  <td className="feature-value">-</td>
                  <td className="feature-value">✓</td>
                  <td className="feature-value">{t('plans.comparison.businessHours')}</td>
                  <td className="feature-value">✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="testimonials-section section">
        <div className="container">
          <h2 className="section-title">{t('plans.testimonialsTitle')}</h2>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <div className="testimonial-content">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="star">⭐</span>
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.content}"</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-info">
                    <h4 className="author-name">{testimonial.name}</h4>
                    <p className="author-role">{testimonial.role}</p>
                    <p className="author-company">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq-section section">
        <div className="container">
          <h2 className="section-title">{t('plans.faqTitle')}</h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlansPage;
